

import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, HelpCircle } from 'lucide-react';
import { cn } from '@/lib/utils';

const FAQ_ITEMS = [
  {
    question: 'How do I get started?',
    answer: 'Create a free account and you\'ll receive 1,000 coins to get started. Head to the Events page to browse active main events and enter for free.',
  },
  {
    question: 'What are main events?',
    answer: 'Main events are tournament-style pick-em competitions built around real sporting events like March Madness. Each event is divided into rounds (undercards) where you pick game winners to earn points.',
  },
  {
    question: 'How do picks work?',
    answer: 'For each round in an event, you\'ll see a list of matchups. Pick the winner of each game before the lock deadline. Correct picks earn points — later rounds are worth more points than earlier ones.',
  },
  {
    question: 'What are props?',
    answer: 'Props are optional coin purchases that boost your maximum points ceiling. For example, you might buy a prop like "Duke wins by 10+" — if it hits, you earn bonus points on top of your picks. Props cost coins but can give you an edge on the leaderboard.',
  },
  {
    question: 'How do I earn coins?',
    answer: 'Every new account starts with 1,000 free coins. You can purchase more at The Mint using real money, or earn coins by placing well in events.',
  },
  {
    question: 'Is it free to play?',
    answer: 'Yes! Entering main events is completely free. Coins are only needed for optional prop boosts that increase your scoring ceiling. You can compete and win without spending anything.',
  },
  {
    question: 'What sports are supported?',
    answer: 'We currently support NCAA Basketball (including March Madness), NBA, NHL, and Soccer. More sports are added regularly based on the season and upcoming tournaments.',
  },
];

export function FAQ() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  return (
    <div className="py-16">
      <div className="text-center mb-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="flex items-center justify-center gap-2 mb-4"
        >
          <HelpCircle className="h-8 w-8 text-gold" />
          <h2 className="text-3xl font-serif font-bold text-cream">FAQ</h2>
        </motion.div>
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.1 }}
          className="text-warm-gray max-w-2xl mx-auto"
        >
          Got questions? We&apos;ve got answers.
        </motion.p>
      </div>

      <div className="max-w-3xl mx-auto space-y-3">
        {FAQ_ITEMS.map((item, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: index * 0.05 }}
          >
            <button
              onClick={() => setOpenIndex(openIndex === index ? null : index)}
              className={cn(
                'w-full p-4 rounded-xl text-left transition-all',
                'border border-parchment-border bg-parchment',
                'hover:border-parchment-dark',
                openIndex === index && 'border-gold/50'
              )}
            >
              <div className="flex items-center justify-between">
                <span className="font-medium text-surface">{item.question}</span>
                <ChevronDown
                  className={cn(
                    'h-5 w-5 text-surface/40 transition-transform',
                    openIndex === index && 'rotate-180 text-gold'
                  )}
                />
              </div>

              <AnimatePresence>
                {openIndex === index && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                    className="overflow-hidden"
                  >
                    <p className="pt-3 text-sm text-surface/60">{item.answer}</p>
                  </motion.div>
                )}
              </AnimatePresence>
            </button>
          </motion.div>
        ))}
      </div>
    </div>
  );
}
